import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

/**
 * Customer 360 data. Staff-only screen, read under the staff RLS bypass.
 *
 * Zoho contacts and portal users are loaded by their own hooks
 * (useContactsForCustomer / useCustomerUsers) so they can be refreshed
 * independently after an invite.
 *
 * audit_log is readable by superadmins only; for other staff the query is
 * rejected by RLS and `auditLog` is simply empty.
 */
export function useCustomerDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['admin-customer-detail', id],
    enabled: !!id,
    queryFn: async () => {
      if (!id) throw new Error('Customer ID required');

      const [customerRes, locationsRes, deploymentsRes, ticketsRes, auditRes, healthRes, tripsRes] = await Promise.all([
        supabase.from('customers').select('*').eq('id', id).single(),
        supabase.from('locations').select('*, deployments(id, solution_type, status, vendor)').eq('customer_id', id).is('deleted_at', null).order('name'),
        supabase.from('deployments').select('*, locations!inner(id, name, customer_id)').eq('locations.customer_id', id).neq('status', 'decommissioned'),
        supabase.from('tickets').select('*, location:locations(id, name), assigned_to:users!tickets_assigned_to_user_id_fkey(id, full_name, email)').eq('customer_id', id).in('status', ['open', 'in_progress', 'waiting_on_customer']).order('created_at', { ascending: false }),
        // The audit trigger stores the table name as entity_type.
        supabase.from('audit_log').select('id, created_at, actor_label, action, entity_type, entity_id').eq('entity_type', 'customers').eq('entity_id', id).order('created_at', { ascending: false }).limit(20),
        supabase.from('customer_health_scores').select('*').eq('customer_id', id).maybeSingle(),
        supabase.from('field_trips').select('*').eq('customer_id', id).order('start_date', { ascending: false }),
      ]);

      if (customerRes.error) throw customerRes.error;
      if (locationsRes.error) throw locationsRes.error;
      if (deploymentsRes.error) throw deploymentsRes.error;
      if (ticketsRes.error) throw ticketsRes.error;

      return {
        customer: customerRes.data,
        locations: locationsRes.data ?? [],
        deployments: deploymentsRes.data ?? [],
        tickets: ticketsRes.data ?? [],
        auditLog: auditRes.data ?? [],
        health: healthRes.data,
        trips: (tripsRes.data ?? []) as Array<Record<string, unknown>>,
      };
    },
    staleTime: 60 * 1000,
  });
}
